<template>
  <div>
    <slot name="filters"></slot>
    <v-data-table
      mobile-breakpoint="0"
      class="appointment-table mt-3 elevation-1"
      :headers="computedHeaders"
      :items="items"
      :options.sync="paginationConfig"
      :server-items-length="totalItems"
      must-sort
      item-key="appointmentId"
      :footer-props="{
        'items-per-page-options': itemsPerPage,
        'items-per-page-text': $t('table.itemsPerPageText')
      }"
    >
      <!-- NO DATA -->
      <template v-slot:no-data>
        <base-table-no-data-no-localized
          :filterCriteriaResults="filterCriteriaResults"
          :activeSortValue="activeSortValue"
          :totalItems="totalItems"
        ></base-table-no-data-no-localized>
      </template>

      <!-- NO SEARCH RESULTS -->
      <template v-slot:no-results>
        <base-table-no-data-no-localized
          :filterCriteriaResults="filterCriteriaResults"
          :activeSortValue="activeSortValue"
          :totalItems="totalItems"
        ></base-table-no-data-no-localized>
      </template>

      <template v-slot:item="{ item }" v-if="isBookingType">
        <appointments-list-item
          :appointment="item"
          :is-selected="isSelected(item)"
          :isBookingType="isBookingType"
          @toggle-select="onToggleSelect"
        ></appointments-list-item>
      </template>

      <!--To avoid redundancy, if the type is "request", do not call isSelected -->
      <template v-slot:item="{ item }" v-else>
        <appointments-list-item
          :appointment="item"
          :isBookingType="isBookingType"
          @toggle-select="onToggleSelect"
        ></appointments-list-item>
      </template>

      <template v-slot:[`footer.page-text`]="item">
        {{
          getVisibleDataMeta(item.pageStart, item.pageStop, item.itemsLength)
        }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import AppointmentsListItem from "./AppointmentsListItem";
import BaseTableNoDataNoLocalized from "./BaseTableNoDataNoLocalized";

export default {
  components: {
    AppointmentsListItem,
    BaseTableNoDataNoLocalized
  },
  props: {
    items: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      tableHeaders: [
        {
          text: this.$t("appointments.sla"),
          value: "slaDueDate",
          sortable: true,
          width: "90px"
        },
        {
          text: this.$t("appointments.member"),
          sortable: false
        },
        {
          text: this.$t("appointments.service"),
          sortable: false,
          width: "270px"
        },
        {
          text: this.$t("appointments.scheduleDate"),
          value: "appointmentScheduleDate",
          sortable: false,
          width: "160px"
        },
        {
          text: this.$t("appointments.acceptedDate"),
          value: "appointmentRequestDate",
          sortable: true,
          width: "160px"
        },
        {
          text: this.$t("appointments.status"),
          sortable: false,
          width: "350px"
        }
      ],
      itemsPerPage: [15, 30, 50, 100],
      selected: []
    };
  },
  created() {
    this.tableHeaders[2].sortable = this.isBookingType;
  },
  methods: {
    getVisibleDataMeta(pageStart, pageStop, itemsLength) {
      const wordOF = this.$t("table.of");
      return `${pageStart} - ${pageStop} ${wordOF} ${itemsLength}`;
    },
    retrieveItems() {
      this.$store.dispatch("appointments/getItems", this.officeId);
    },
    isPaginationUpdated(paginationConfig) {
      return (
        this.order.sortBy[0] != paginationConfig.sortBy[0] ||
        this.order.descending[0] != paginationConfig.sortDesc[0] ||
        this.page != paginationConfig.page ||
        this.size != paginationConfig.itemsPerPage
      );
    },
    isSelected(appointmentId) {
      const ids = this.$store.getters["appointments/getSelectedAppointmentIds"];
      return ids.includes(appointmentId);
    },
    onToggleSelect(appointmentId) {
      const ids = this.$store.getters["appointments/getSelectedAppointmentIds"];
      const i = ids.findIndex(s => s === appointmentId);
      if (i >= 0) {
        // remove from list
        this.$store.commit(
          "appointments/removeIdFromSelectedAppointmentIds",
          i
        );
      } else {
        // add into list
        this.$store.commit(
          "appointments/addIdToSelectedAppointmentIds",
          appointmentId
        );
      }
    }
  },
  computed: {
    computedHeaders() {
      //add blank header to align checkboxes when the type is booking
      if (this.isBookingType) {
        return [
          { text: "", value: "checkboxSpacer", sortable: false, width: "40px" },
          ...this.tableHeaders
        ];
      }
      return this.tableHeaders;
    },
    officeId() {
      return this.$store.getters["hfManagement/officeId"];
    },
    isBookingType() {
      let appointmentType = this.$store.getters["appointments/type"];
      return appointmentType === "booking";
    },
    paginationConfig: {
      get() {
        return {
          page: this.page,
          itemsPerPage: this.size,
          sortBy: this.order.sortBy,
          sortDesc: this.order.descending
        };
      },
      set(paginationConfig) {
        if (this.isPaginationUpdated(paginationConfig)) {
          this.order = {
            descending: paginationConfig.sortDesc,
            sortBy: paginationConfig.sortBy
          };
          this.size = paginationConfig.itemsPerPage;
          this.page = paginationConfig.page;
          this.retrieveItems();
        }
      }
    },
    page: {
      get() {
        return this.$store.getters["appointments/page"];
      },
      set(page) {
        this.$store.commit("appointments/updatePage", page);
      }
    },
    size: {
      get() {
        return this.$store.getters["appointments/size"];
      },
      set(size) {
        this.$store.commit("appointments/updateSize", size);
      }
    },
    order: {
      get() {
        return this.$store.getters["appointments/order"];
      },
      set(order) {
        this.$store.commit("appointments/SET_ORDER", order);
      }
    },
    totalItems() {
      return this.$store.getters["appointments/totalItems"];
    },
    filterCriteriaResults() {
      return this.$store.getters[`appointments/filterCriteriaResults`];
    },
    activeSortValue() {
      return this.$store.getters[`appointments/activeSortValue`];
    }
  }
};
</script>
